'use client';

/**
 * GenerativeAvatarCustomizer
 *
 * Popover panel for admins / creators to choose the pattern + colours of an
 * artist's generative fallback avatar (shown when no photo exists).
 *
 * Config saved to `artists/{id}.avatarConfig`:
 *   { patternIndex: number | null, bgColor: string | null, fgColor: string | null }
 *
 * Per Gordon's spec:
 *   • Pattern picker is live (clicking a pattern actually changes the avatar).
 *   • Background picker defaults to TRANSPARENT — page background shows through.
 *   • Once a coloured (non-transparent) background is picked, a line / text
 *     colour picker appears so the strokes contrast properly with the bg.
 */

import { useEffect, useRef, useState } from 'react';
import { Wand2, Check, Loader2, Dices, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { GenerativeArtistAvatar, PATTERN_NAMES } from '@/components/media/generative-artist-avatar';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/auth-context';
import {
  DISPLAY_GOOGLE_FONTS, ensureFontLoaded, loadAllCloudFonts, uploadCloudFont,
} from '@/lib/fonts';

interface AvatarConfigSnapshot {
  patternIndex: number | null;
  bgColor: string | null;
  fgColor: string | null;
  /** Google font family or a cloud custom-font family; null = Impact stack. */
  fontFamily?: string | null;
}

interface Props {
  artistId: string;
  artistName: string;
  /** Current saved config — used to prefill state on open. */
  currentConfig?: Partial<AvatarConfigSnapshot>;
  /** Legacy single fields, still accepted so the existing call site doesn't break. */
  currentPattern?: number;
  currentPalette?: number;
  onSave?: (config: AvatarConfigSnapshot) => void;
  compact?: boolean;
}

/** Quick background swatches. First entry is the canonical "transparent". */
const BG_SWATCHES: { value: string; label: string }[] = [
  { value: 'transparent', label: 'Transparent' },
  { value: 'theme',       label: 'Theme bg' },
  { value: '#0d1117',     label: 'Black' },
  { value: '#ffffff',     label: 'White' },
  { value: '#1e3a5f',     label: 'Indigo' },
  { value: '#1c2b1f',     label: 'Forest' },
  { value: '#3b1a1a',     label: 'Brick' },
  { value: '#f5d76e',     label: 'Mustard' },
];

/** Line / text colour swatches. Default is theme foreground via `null`. */
const FG_SWATCHES: { value: string; label: string }[] = [
  { value: 'auto',     label: 'Auto' },
  { value: '#ffffff',  label: 'White' },
  { value: '#0d1117',  label: 'Black' },
  { value: '#f5d76e',  label: 'Mustard' },
  { value: '#88c0d0',  label: 'Sky' },
  { value: '#ff6b6b',  label: 'Coral' },
  { value: '#9cffb6',  label: 'Mint' },
  { value: '#c4a3ff',  label: 'Lilac' },
];

export function GenerativeAvatarCustomizer({
  artistId,
  artistName,
  currentConfig,
  currentPattern,
  currentPalette: _currentPalette, // legacy, ignored
  onSave,
  compact = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  /** Pattern index. -1 = Auto (seeded from name). */
  const [patternIndex, setPatternIndex] = useState<number>(
    currentConfig?.patternIndex ?? currentPattern ?? -1,
  );
  /** Background — "transparent" (default), "theme", or a CSS colour string. */
  const [bgColor, setBgColor] = useState<string>(
    currentConfig?.bgColor ?? 'transparent',
  );
  /** Foreground / line / text colour. "auto" = theme foreground. */
  const [fgColor, setFgColor] = useState<string>(
    currentConfig?.fgColor ?? 'auto',
  );
  /** Lettering font. '' = default Impact stack. */
  const [fontFamily, setFontFamily] = useState<string>(currentConfig?.fontFamily || '');
  /** Cloud custom-font families (dafont-style uploads shared via Firestore). */
  const [cloudFamilies, setCloudFamilies] = useState<string[]>([]);
  const [uploadingFont, setUploadingFont] = useState(false);
  const fontFileRef = useRef<HTMLInputElement>(null);
  const { user, userProfile } = useAuth();
  const canUploadFonts = ['admin', 'superadmin', 'creator'].includes(String(userProfile?.accountType || ''));

  // Load cloud fonts + the currently-saved font when the panel opens.
  useEffect(() => {
    if (!open) return;
    loadAllCloudFonts().then(setCloudFamilies).catch(() => {});
    if (fontFamily) void ensureFontLoaded(fontFamily);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fontChoices = ['', ...DISPLAY_GOOGLE_FONTS, ...cloudFamilies.filter(f => !DISPLAY_GOOGLE_FONTS.includes(f))];

  const pickFont = async (f: string) => {
    if (f) await ensureFontLoaded(f);
    setFontFamily(f);
  };

  const handleRandomize = async () => {
    const pat = Math.floor(Math.random() * PATTERN_NAMES.length);
    const bg = BG_SWATCHES[Math.floor(Math.random() * BG_SWATCHES.length)].value;
    const fg = bg === 'transparent' ? 'auto' : FG_SWATCHES[Math.floor(Math.random() * FG_SWATCHES.length)].value;
    const font = fontChoices[Math.floor(Math.random() * fontChoices.length)];
    if (font) await ensureFontLoaded(font);
    setPatternIndex(pat); setBgColor(bg); setFgColor(fg); setFontFamily(font);
  };

  const handleFontUpload = async (file: File | null) => {
    if (!file) return;
    setUploadingFont(true);
    try {
      const font = await uploadCloudFont(file, user?.uid);
      setCloudFamilies(prev => prev.includes(font.family) ? prev : [...prev, font.family]);
      setFontFamily(font.family);
    } catch (err) {
      console.error('Font upload failed', err);
    } finally {
      setUploadingFont(false);
      if (fontFileRef.current) fontFileRef.current.value = '';
    }
  };

  const effectivePattern = patternIndex >= 0 ? patternIndex : undefined;
  const effectiveBg = bgColor;
  const effectiveFg = fgColor === 'auto' ? undefined : fgColor;
  const fgPickerVisible = bgColor !== 'transparent';

  const handleSave = async () => {
    setSaving(true);
    try {
      const snapshot: AvatarConfigSnapshot = {
        patternIndex: patternIndex >= 0 ? patternIndex : null,
        bgColor: bgColor === 'transparent' ? null : bgColor,
        fgColor: fgColor === 'auto' ? null : fgColor,
        fontFamily: fontFamily || null,
      };
      await updateDoc(doc(db, 'artists', artistId), { avatarConfig: snapshot });
      onSave?.(snapshot);
      setOpen(false);
    } catch (err) {
      console.error('Failed to save avatar config', err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'artists', artistId), { avatarConfig: null });
      setPatternIndex(-1);
      setBgColor('transparent');
      setFgColor('auto');
      setFontFamily('');
      onSave?.({ patternIndex: null, bgColor: null, fgColor: null, fontFamily: null });
      setOpen(false);
    } catch (err) {
      console.error('Failed to reset avatar config', err);
    } finally {
      setSaving(false);
    }
  };

  /** Tiny swatch button with a checkmark when selected. */
  const Swatch = ({ value, active, onClick, transparent }: { value: string; active: boolean; onClick: () => void; transparent?: boolean }) => (
    <button
      onClick={onClick}
      className={cn(
        'h-7 w-7 rounded-md relative transition-all flex-shrink-0',
        active ? 'ring-2 ring-violet-400 ring-offset-1 ring-offset-background' : 'ring-1 ring-border/40 hover:ring-border',
      )}
      style={transparent
        ? {
            backgroundImage:
              'linear-gradient(45deg, hsl(var(--muted)) 25%, transparent 25%, transparent 75%, hsl(var(--muted)) 75%),' +
              'linear-gradient(45deg, hsl(var(--muted)) 25%, transparent 25%, transparent 75%, hsl(var(--muted)) 75%)',
            backgroundSize: '8px 8px',
            backgroundPosition: '0 0, 4px 4px',
          }
        : { background: value === 'theme' ? 'hsl(var(--background))' : value }}
      title={value}
      aria-pressed={active}
    >
      {active && <Check className="h-3 w-3 text-violet-300 absolute inset-0 m-auto drop-shadow" />}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size={compact ? 'icon' : 'sm'}
          className={cn('gap-1.5 text-xs', compact ? 'h-8 w-8' : 'h-7')}
          title="Customize generative avatar"
        >
          <Wand2 className={cn('text-violet-400', compact ? 'h-4 w-4' : 'h-3.5 w-3.5')} />
          {!compact && <span>Customize Avatar</span>}
        </Button>
      </DialogTrigger>
      {/* Was an anchored Popover — on a trigger near the top of the page its
          tall content (preview + pattern grid + swatches + font row) had
          nowhere to expand and got clipped off the top of the viewport
          (Gordon 2026-09-30: "fx popup window is hidden at the top. make it
          pop up in the center" / "maybe make it wider in desktop"). Dialog
          centers itself and caps to the viewport with internal scroll, so
          neither can happen again. */}
      <DialogContent className="sm:max-w-2xl p-4 space-y-3 bg-background/95 backdrop-blur-md border-border/60">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5 text-sm">
            <Wand2 className="h-4 w-4 text-violet-400" /> Customize Avatar
          </DialogTitle>
        </DialogHeader>
        {/* Live preview */}
        <div
          className="relative rounded-lg overflow-hidden aspect-square w-full max-w-[160px] mx-auto"
          // Show a checkerboard behind the preview so transparent backgrounds
          // are immediately distinguishable from "theme" or any solid fill.
          style={{
            backgroundImage:
              'linear-gradient(45deg, hsl(var(--muted)) 25%, transparent 25%, transparent 75%, hsl(var(--muted)) 75%),' +
              'linear-gradient(45deg, hsl(var(--muted)) 25%, transparent 25%, transparent 75%, hsl(var(--muted)) 75%)',
            backgroundSize: '16px 16px',
            backgroundPosition: '0 0, 8px 8px',
          }}
        >
          <GenerativeArtistAvatar
            name={artistName}
            size={160}
            className="absolute inset-0 w-full h-full"
            animate
            patternOverride={effectivePattern}
            bgColor={effectiveBg}
            fgColor={effectiveFg}
            fontFamily={fontFamily || undefined}
          />
        </div>

        {/* Pattern selector */}
        <div>
          <p className="text-xs text-muted-foreground mb-1.5 font-medium uppercase tracking-wide">Pattern</p>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
            {/* Auto tile */}
            <button
              onClick={() => setPatternIndex(-1)}
              className={cn(
                'relative rounded-md overflow-hidden aspect-square text-[9px] font-medium transition-all flex items-center justify-center',
                patternIndex === -1
                  ? 'ring-2 ring-violet-400 ring-offset-1 ring-offset-background bg-violet-500/15 text-violet-200'
                  : 'ring-1 ring-border/30 hover:ring-border bg-muted/40 text-muted-foreground',
              )}
              title="Auto — seeded from name"
            >
              Auto
            </button>
            {PATTERN_NAMES.map((pname, idx) => (
              <button
                key={idx}
                onClick={() => setPatternIndex(idx === patternIndex ? -1 : idx)}
                className={cn(
                  'relative rounded-md overflow-hidden aspect-square text-[9px] font-medium transition-all',
                  patternIndex === idx
                    ? 'ring-2 ring-violet-400 ring-offset-1 ring-offset-background'
                    : 'ring-1 ring-border/30 hover:ring-border',
                )}
                title={pname}
              >
                <GenerativeArtistAvatar
                  name={artistName}
                  size={72}
                  className="absolute inset-0 w-full h-full"
                  animate={false}
                  patternOverride={idx}
                  bgColor={effectiveBg}
                  fgColor={effectiveFg}
                />
                {patternIndex === idx && (
                  <div className="absolute inset-x-0 bottom-0 px-1 py-0.5 bg-black/50 text-violet-200 truncate">
                    {pname}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Background colour */}
        <div>
          <p className="text-xs text-muted-foreground mb-1.5 font-medium uppercase tracking-wide">
            Background
            <span className="ml-1 normal-case text-[10px] opacity-70">
              · default transparent
            </span>
          </p>
          <div className="flex gap-1.5 flex-wrap">
            {BG_SWATCHES.map(s => (
              <Swatch
                key={s.value}
                value={s.value}
                active={bgColor === s.value}
                onClick={() => setBgColor(s.value)}
                transparent={s.value === 'transparent'}
              />
            ))}
            {/* Custom colour picker */}
            <label className="h-7 w-7 rounded-md ring-1 ring-border/40 hover:ring-border overflow-hidden cursor-pointer flex items-center justify-center text-[10px] font-bold relative">
              <input
                type="color"
                value={bgColor.startsWith('#') ? bgColor : '#000000'}
                onChange={e => setBgColor(e.target.value)}
                className="absolute inset-0 opacity-0 cursor-pointer"
                aria-label="Custom background colour"
              />
              <span aria-hidden>+</span>
            </label>
          </div>
        </div>

        {/* Foreground / line colour — only when bg is non-transparent */}
        {fgPickerVisible && (
          <div>
            <p className="text-xs text-muted-foreground mb-1.5 font-medium uppercase tracking-wide">
              Vector + text colour
            </p>
            <div className="flex gap-1.5 flex-wrap">
              {FG_SWATCHES.map(s => (
                <Swatch
                  key={s.value}
                  value={s.value === 'auto' ? 'hsl(var(--foreground))' : s.value}
                  active={fgColor === s.value}
                  onClick={() => setFgColor(s.value)}
                />
              ))}
              <label className="h-7 w-7 rounded-md ring-1 ring-border/40 hover:ring-border overflow-hidden cursor-pointer flex items-center justify-center text-[10px] font-bold relative">
                <input
                  type="color"
                  value={fgColor.startsWith('#') ? fgColor : '#ffffff'}
                  onChange={e => setFgColor(e.target.value)}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  aria-label="Custom line colour"
                />
                <span aria-hidden>+</span>
              </label>
            </div>
          </div>
        )}

        {/* Font — Google display fonts + shared cloud fonts (dafont-style
            uploads). Each chip previews in its own typeface. */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Font</p>
            {canUploadFonts && (
              <>
                <input
                  ref={fontFileRef} type="file" accept=".ttf,.otf,.woff,.woff2" className="hidden"
                  onChange={e => handleFontUpload(e.target.files?.[0] || null)}
                />
                <button
                  onClick={() => fontFileRef.current?.click()}
                  disabled={uploadingFont}
                  className="flex items-center gap-1 text-[10px] font-semibold text-violet-300 hover:text-violet-200 disabled:opacity-50"
                  title="Import a .ttf/.otf/.woff font file (e.g. from dafont.com — check its licence). Shared with Artwork Studio."
                >
                  {uploadingFont ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                  Import font
                </button>
              </>
            )}
          </div>
          <div
            className="flex gap-1.5 overflow-x-auto pb-1 hide-scrollbar focus:outline-none focus:ring-1 focus:ring-violet-500/50 rounded-md p-1"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                const idx = fontChoices.indexOf(fontFamily || '');
                const dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : -1;
                const nextIdx = idx !== -1
                  ? (idx + dir + fontChoices.length) % fontChoices.length
                  : (dir === 1 ? 0 : fontChoices.length - 1);
                const nextFont = fontChoices[nextIdx];
                pickFont(nextFont);
              }
            }}
          >
            {fontChoices.map(f => (
              <button
                key={f || 'default'}
                onClick={() => pickFont(f)}
                className={cn(
                  'flex-shrink-0 px-2.5 h-8 rounded-md text-sm whitespace-nowrap transition-all',
                  fontFamily === f
                    ? 'ring-2 ring-violet-400 ring-offset-1 ring-offset-background bg-violet-500/15 font-semibold'
                    : 'ring-1 ring-border/40 hover:ring-border bg-muted/30',
                )}
                style={{ fontFamily: f ? `'${f}', sans-serif` : 'Impact, sans-serif' }}
                onMouseEnter={() => { if (f) void ensureFontLoaded(f); }}
                title={f || 'Impact (default)'}
              >
                {f || 'Impact'}
              </button>
            ))}
          </div>
        </div>

        {/* Actions — Save · New (random) · Erase */}
        <div className="flex gap-2 pt-1 border-t border-border/40">
          <Button size="sm" className="flex-1 h-7 text-xs bg-violet-600 hover:bg-violet-500 text-white" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={handleRandomize} disabled={saving}
            title="Roll a brand-new artwork (random pattern, colours + font)">
            <Dices className="h-3.5 w-3.5" /> New
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-red-400 hover:text-red-300 hover:bg-red-500/10" onClick={handleReset} disabled={saving}
            title="Erase the saved artwork — falls back to the automatic one">
            <Trash2 className="h-3.5 w-3.5" /> Erase
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
